import asyncPool from 'tiny-async-pool';
import * as _ from 'lodash';
import { type MondayClient, extractApiResult, MondayRequesterError, AbortedError, getAllResults } from './common';

export type AutoNumberColumnType = 'autonumber';
export type ButtonColumnType = 'button';
export type BooleanColumnType = 'checkbox';
export type ColorPickerColumnType = 'color_picker';
export type BoardRelationColumnType = 'board_relation';
export type CountryColumnType = 'country';
export type PulseLogColumnType = 'creation_log';
export type DateColumnType = 'date';
export type DependencyColumnType = 'dependency';
export type DropdownColumnType = 'dropdown';
export type EmailColumnType = 'email';
export type FileColumnType = 'file';
export type FormulaColumnType = 'formula';
export type HourColumnType = 'hour';
export type PulseIdColumnType = 'item_id';
export type PulseUpdatedColumnType = 'last_updated';
export type LinkColumnType = 'link';
export type LongTextColumnType = 'long_text';
export type MultiplePersonColumnType = 'people';
export type PhoneColumnType = 'phone';
export type RatingColumnType = 'rating';
export type LocationColumnType = 'location';
export type MirrorColumnType = 'mirror';
export type NumericColumnType = 'numbers';
export type ProgressTrackingColumnType = 'progress'; // TODO: double check if this is correct
export type StatusColumnType = 'status';
export type TagsColumnType = 'tags';
export type TeamColumnType = 'team';
export type TextColumnType = 'text';
export type TimerangeColumnType = 'timeline';
export type DurationColumnType = 'time_tracking';
export type VotesColumnType = 'vote';
export type WeekColumnType = 'week';
export type TimezoneColumnType = 'world_clock';
export type SubtasksColumnType = 'subtasks';

export type AnyBaseColumnType =
  | AutoNumberColumnType
  | ButtonColumnType
  | BooleanColumnType
  | ColorPickerColumnType
  | CountryColumnType
  | PulseLogColumnType
  | DateColumnType
  | DropdownColumnType
  | EmailColumnType
  | FormulaColumnType
  | HourColumnType
  | PulseIdColumnType
  | PulseUpdatedColumnType
  | LinkColumnType
  | LongTextColumnType
  | MultiplePersonColumnType
  | PhoneColumnType
  | RatingColumnType
  | LocationColumnType
  | NumericColumnType
  | ProgressTrackingColumnType
  | StatusColumnType
  | TeamColumnType
  | TextColumnType
  | TimerangeColumnType
  | DurationColumnType
  | VotesColumnType
  | WeekColumnType
  | TimezoneColumnType
  | SubtasksColumnType;

export type ColumnType =
  AnyBaseColumnType | BoardRelationColumnType | DependencyColumnType | MirrorColumnType | TagsColumnType | FileColumnType;

type AbstractBoardColumnValue = {
  id: string;
  text: string | null;
  value: string | null;
  display_value?: string;
};

type BaseBoardColumnValue = AbstractBoardColumnValue & {
  type: AnyBaseColumnType;
};

export type BoardRelationColumnValue = AbstractBoardColumnValue & {
  type: BoardRelationColumnType;
  display_value: string;
  linked_item_ids: string[];
};

export type DependencyColumnValue = AbstractBoardColumnValue & {
  type: DependencyColumnType;
  display_value: string;
};

export type MirrorColumnValue = AbstractBoardColumnValue & {
  type: MirrorColumnType;
  display_value: string;
};

export type TagsColumnValue = AbstractBoardColumnValue & {
  type: TagsColumnType;
  text: string;
};

export type FileColumnValue = AbstractBoardColumnValue & {
  type: FileColumnType;
  files: string[];
};

export type BoardColumnValue =
  BaseBoardColumnValue | BoardRelationColumnValue | DependencyColumnValue | MirrorColumnValue | TagsColumnValue | FileColumnValue;

function idToNumber<T extends { id: string | number }>(obj: T) {
  if (!obj || !obj.id) {
    return obj;
  }

  return { ...obj, id: _.toNumber(obj.id) };
}

function transformBoardItem(item: BoardItem) {
  if (item.parent_item && item.parent_item.id) {
    item.parent_item.id = _.toNumber(item.parent_item.id);
  }

  item.id = _.toNumber(item.id);

  if (item.subitems) {
    item.subitems = item.subitems.map((subitem) => transformBoardItem(subitem));
  }

  return item;
}

export type Asset = {
  created_at: string;
  file_extension: string;
  file_size: number;
  id: string;
  name: string;
  original_geometry?: string | null;
  url: string;
  url_thumbnail: string;
  public_url: string;
};

const assetBaseFields = `
  created_at
  file_extension
  file_size
  id
  name
  original_geometry
  public_url
  url
  url_thumbnail
`;

export type BoardItem = {
  id: number;
  created_at: string;
  creator_id: string;
  name: string;
  state: 'all' | 'active' | 'archived' | 'deleted';
  updated_at: string;
  email: string;
  relative_link: string;
  group: {
    id: string;
    title: string;
  };
  parent_item: {
    id: number;
  } | null;
  column_values: BoardColumnValue[];
  subitems?: BoardItem[];
};

export type BaseBoardFieldsOptions = {
  withoutBoardRelationValue?: boolean;
  withoutMirrorValue?: boolean;
  columnIds?: string[]; // if it's an empty array, all column values will be returned
  withFormulaValue?: boolean;
  withAssetsValue?: boolean;
};

function getBaseBoardFields(options: BaseBoardFieldsOptions = {}) {
  return `
    id
    created_at
    creator_id
    name
    state
    updated_at
    email
    relative_link
    group {
      id
      title
    }
    parent_item {
      id
    }
    column_values ${Array.isArray(options.columnIds) ? `(ids: [${options.columnIds.map((id) => `"${id}"`).join(', ')}])` : ''} {
      id
      text
      type
      value
      ${options.withoutBoardRelationValue ? '' : '... on BoardRelationValue { display_value linked_item_ids }'}
      ${options.withoutMirrorValue ? '' : '... on MirrorValue { display_value }'}
      ${options.withFormulaValue ? '... on FormulaValue { display_value }' : ''}
      ... on DependencyValue {
        display_value
      }
      ... on TagsValue {
        text
      }
      ${
        options.withAssetsValue
          ? `
      ... on FileValue {
        files {
          ... on FileAssetValue {
            asset_id
            asset {
              ${assetBaseFields}
            }
          }
        }
      }
      `
          : ''
      }
    }
  `;
}

export type AdditionalGraphQLVariable = {
  variableName: string;
  variableType: string;
  variableValue: string;
};

export type GetBoardItemsOptionsWithSubitems = {
  withoutBoardRelationValue?: boolean;
  withoutMirrorValue?: boolean;
  withoutSubitemsBoardRelationValue?: boolean;
  withoutSubitemsMirrorValue?: boolean;
  withFormulaValue?: boolean;
  withSubitemsFormulaValue?: boolean;
  withAssetsValue?: boolean;
  withSubitemsAssetsValue?: boolean;
  columnIds?: string[];
  subitemsColumnIds?: string[];
};

export async function getFirstBoardItemsPage(
  mondayClient: MondayClient,
  boardId: number,
  limit: number,
  queryParams?: string,
  withSubitems = false,
  baseBoardFieldsOptions: GetBoardItemsOptionsWithSubitems = {},
  additionalGraphQLVariables: AdditionalGraphQLVariable[] = [],
) {
  const additionalVariablesArguments = additionalGraphQLVariables
    .map((entry) => `$${entry.variableName}: ${entry.variableType}`)
    .join(', ');
  const additionalVariablesValues = additionalGraphQLVariables.reduce(
    (acc, entry) => ({ ...acc, [entry.variableName]: entry.variableValue }),
    {},
  );

  const result = await mondayClient.query(
    `
    query getFirstBoardItems($boardId: ID!, $limit: Int!${additionalVariablesArguments.length > 0 ? `, ${additionalVariablesArguments}` : ''}) {
      complexity {
        query
      }
      boards(ids: [$boardId]) {
        items_page (limit: $limit${queryParams ? `, query_params: ${queryParams}` : ''}) {
          cursor
          items {
            ${getBaseBoardFields(baseBoardFieldsOptions)}
            ${
              withSubitems
                ? `subitems {
              ${getBaseBoardFields({
                withoutBoardRelationValue: baseBoardFieldsOptions.withoutSubitemsBoardRelationValue,
                withoutMirrorValue: baseBoardFieldsOptions.withoutSubitemsMirrorValue,
                withFormulaValue: baseBoardFieldsOptions.withSubitemsFormulaValue,
                withAssetsValue: baseBoardFieldsOptions.withSubitemsAssetsValue,
                columnIds: baseBoardFieldsOptions.subitemsColumnIds,
              })}
            }`
                : ''
            }
          }
        }
      }
    }
  `,
    { boardId, limit, ...additionalVariablesValues },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  // TODO: test what happens if there is only one item in the board. is cursor null?
  return extractApiResult<{ cursor: string | null; items: BoardItem[] }>(result, 'boards[0].items_page', (data) => ({
    ...data,
    items: data.items.map((item) => transformBoardItem(item)),
  }));
}

export type AggregateFunction = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'median';

export type AggregateColumnRequest = {
  columnId: string;
  fn: AggregateFunction;
};

export type AggregateColumnResult = {
  columnId: string;
  fn: AggregateFunction;
  value: number | null;
};

// Maps our internal aggregate function names to the monday.com aggregate API enum values.
// Note: monday uses AVERAGE (not AVG), and COUNT counts items with a non-null value in the column.
// See https://developer.monday.com/api-reference/reference/aggregate
const AGGREGATE_FUNCTION_API_NAMES: Record<AggregateFunction, string> = {
  sum: 'SUM',
  avg: 'AVERAGE',
  min: 'MIN',
  max: 'MAX',
  count: 'COUNT',
  median: 'MEDIAN',
};

// The `aggregate` object was introduced in monday API version 2026-01, so it must be requested
// explicitly (the rest of the app defaults to an older version).
const AGGREGATE_API_VERSION = '2026-01';

/**
 * Computes aggregate values (sum/avg/min/max/count/median) for one or more columns across the whole board,
 * using monday.com's aggregate API. The optional `queryParams` is the same ItemsQuery GraphQL
 * string used for items_page query_params (produced by transformBoardFiltersToGraphQL), so the
 * aggregate respects the exact same filters as the rendered item list.
 */
export async function aggregateColumns(
  mondayClient: MondayClient,
  boardId: number,
  columns: AggregateColumnRequest[],
  queryParams?: string,
  additionalGraphQLVariables: AdditionalGraphQLVariable[] = [],
): Promise<AggregateColumnResult[]> {
  if (!columns.length) {
    return [];
  }

  const additionalVariablesArguments = additionalGraphQLVariables
    .map((entry) => `$${entry.variableName}: ${entry.variableType}`)
    .join(', ');
  const additionalVariablesValues = additionalGraphQLVariables.reduce(
    (acc, entry) => ({ ...acc, [entry.variableName]: entry.variableValue }),
    {},
  );

  const selects = columns
    .map(
      (column, index) =>
        `{ type: FUNCTION, function: { function: ${AGGREGATE_FUNCTION_API_NAMES[column.fn]}, params: [{ type: COLUMN, column: { column_id: "${column.columnId}" }, as: "col_${index}" }] }, as: "agg_${index}" }`,
    )
    .join(', ');

  const result = await mondayClient.query(
    `
    query aggregateColumns($boardId: ID!${additionalVariablesArguments.length > 0 ? `, ${additionalVariablesArguments}` : ''}) {
      complexity {
        query
      }
      aggregate(query: {
        from: { type: TABLE, id: $boardId }${queryParams ? `,\n        query: ${queryParams}` : ''},
        select: [${selects}]
      }) {
        results {
          entries {
            alias
            value {
              ... on AggregateBasicAggregationResult {
                result
              }
            }
          }
        }
      }
    }
  `,
    { boardId, ...additionalVariablesValues },
    undefined,
    { apiVersion: AGGREGATE_API_VERSION },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  const aggregateData = extractApiResult<{
    results: Array<{ entries: Array<{ alias: string; value: { result?: number | string | null } | null }> }>;
  }>(result, 'aggregate');

  const valuesByAlias = new Map<string, number | string | null | undefined>();

  for (const resultRow of aggregateData?.results || []) {
    for (const entry of resultRow.entries || []) {
      valuesByAlias.set(entry.alias, entry.value?.result);
    }
  }

  return columns.map((column, index) => {
    const rawValue = valuesByAlias.get(`agg_${index}`);
    const numericValue = typeof rawValue === 'string' ? Number(rawValue) : rawValue;

    return {
      columnId: column.columnId,
      fn: column.fn,
      value: typeof numericValue === 'number' && Number.isFinite(numericValue) ? numericValue : null,
    };
  });
}

export async function getNextBoardItems(
  mondayClient: MondayClient,
  cursor: string,
  limit: number,
  withSubitems = false,
  baseBoardFieldsOptions: GetBoardItemsOptionsWithSubitems = {},
) {
  const result = await mondayClient.query(
    `
    query getNextBoardItems($cursor: String!, $limit: Int!) {
      complexity {
        query
      }
      next_items_page(cursor: $cursor, limit: $limit) {
        cursor
        items {
          ${getBaseBoardFields(baseBoardFieldsOptions)}
          ${
            withSubitems
              ? `subitems {
              ${getBaseBoardFields({
                withoutBoardRelationValue: baseBoardFieldsOptions.withoutSubitemsBoardRelationValue,
                withoutMirrorValue: baseBoardFieldsOptions.withoutSubitemsMirrorValue,
                columnIds: baseBoardFieldsOptions.subitemsColumnIds,
              })}
            }`
              : ''
          }
        }
      }
    }
  `,
    { cursor, limit },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<BoardItem[]>(result, 'next_items_page.items', (items) => items.map((item: any) => transformBoardItem(item)));
}

async function getNextBoardItemsWithRetries(
  mondayClient: MondayClient,
  cursor: string,
  limit: number,
  retries: number = 3,
  withSubitems = false,
  baseBoardFieldsOptions: GetBoardItemsOptionsWithSubitems = {},
) {
  let currentRetries = 0;
  let lastError: any;

  while (currentRetries < retries) {
    try {
      return await getNextBoardItems(mondayClient, cursor, limit, withSubitems, baseBoardFieldsOptions);
    } catch (err) {
      lastError = err;
      currentRetries++;
    }

    await new Promise((resolve) => setTimeout(resolve, currentRetries * 5000));
  }

  throw lastError;
}

export async function getNextBoardItemsPage(
  mondayClient: MondayClient,
  cursor: string,
  limit: number,
  withSubitems = false,
  baseBoardFieldsOptions: GetBoardItemsOptionsWithSubitems = {},
) {
  const result = await mondayClient.query(
    `
    query getNextBoardItems($cursor: String!, $limit: Int!) {
      complexity {
        query
      }
      next_items_page(cursor: $cursor, limit: $limit) {
        cursor
        items {
          ${getBaseBoardFields(baseBoardFieldsOptions)}
          ${
            withSubitems
              ? `subitems {
            ${getBaseBoardFields({
              withoutBoardRelationValue: baseBoardFieldsOptions.withoutSubitemsBoardRelationValue,
              withoutMirrorValue: baseBoardFieldsOptions.withoutSubitemsMirrorValue,
            })}
          }`
              : ''
          }
        }
      }
    }
  `,
    { cursor, limit },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  // TODO: test what happens if there is only one item in the board. is cursor null?
  return extractApiResult<{ cursor: string | null; items: BoardItem[] }>(result, 'next_items_page', (data) => ({
    ...data,
    items: data.items.map((item) => transformBoardItem(item)),
  }));
}

async function getAllCursors(mondayClient: MondayClient, firstCursor: string, limit: number, abortSignal?: AbortSignal) {
  const cursors: Array<string> = [];

  let currentCursor = firstCursor;

  while (currentCursor) {
    cursors.push(currentCursor);

    if (abortSignal?.aborted) {
      throw new AbortedError('Aborted loading all cursors');
    }

    const result = await mondayClient.query(
      `
      query getNextPageCursor($cursor: String!, $limit: Int!) {
        complexity {
          query
        }
        next_items_page(cursor: $cursor, limit: $limit) {
          cursor
        }
      }
    `,
      { cursor: currentCursor, limit },
    );

    if (result.type === 'error') {
      throw new MondayRequesterError(result);
    }

    currentCursor = _.get(result, 'data.next_items_page.cursor') as string;
  }

  return cursors;
}

export type Metric = {
  name: string;
  value: number;
};

type GetBoardItemsOptions = {
  itemsPerPage: number;
  abortSignal?: AbortSignal;
  queryParams?: string;
  onProgress: (progress: number) => void;
  maxParallelRequests: number;
  removeDuplicates: boolean;
  baseBoardFieldsOptions?: GetBoardItemsOptionsWithSubitems;
  logger?: (message: string, type: 'info' | 'warning' | 'error') => void;
  withSubitems?: boolean;
  onMetric?: (metric: Metric) => void;
};

const getBoardItemsDefaultOptions: GetBoardItemsOptions = {
  itemsPerPage: 250,
  abortSignal: undefined,
  queryParams: undefined,
  onProgress: () => {},
  maxParallelRequests: 50,
  removeDuplicates: true,
  baseBoardFieldsOptions: {},
  withSubitems: false,
  onMetric: undefined,
  logger: undefined,
};

export async function getBoardItems(mondayClient: MondayClient, boardId: number, optionsArg: Partial<GetBoardItemsOptions> = {}) {
  const options = { ...getBoardItemsDefaultOptions, ...optionsArg };
  const allItems: BoardItem[] = [];
  let progress = 0;

  if (options.logger) {
    options.logger(
      `Fetching items of board ${boardId} [itemsPerPage=${options.itemsPerPage}, maxParallelRequests=${options.maxParallelRequests}, withoutBoardRelationValue=${!!options.baseBoardFieldsOptions?.withoutBoardRelationValue}, withoutMirrorValue=${!!options.baseBoardFieldsOptions?.withoutMirrorValue}]`,
      'info',
    );
  }

  const getFirstResultStart = Date.now();

  const firstResult = await getFirstBoardItemsPage(
    mondayClient,
    boardId,
    1,
    options.queryParams,
    options.withSubitems,
    options.baseBoardFieldsOptions,
  );

  if (options.onMetric) {
    options.onMetric({
      name: 'getFirstResult',
      value: Date.now() - getFirstResultStart,
    });
  }

  allItems.push(...firstResult.items);

  progress = 5;
  options.onProgress(progress);

  if (options.abortSignal?.aborted) {
    throw new AbortedError('Aborted loading board items');
  }

  const getAllCursorsStart = Date.now();

  // @ts-ignore
  const cursors = await getAllCursors(mondayClient, firstResult.cursor, options.itemsPerPage, options?.abortSignal);

  if (options.logger) {
    options.logger(`Fetched ${cursors.length} cursors`, 'info');
  }

  if (options.onMetric) {
    options.onMetric({
      name: 'getAllCursors',
      value: Date.now() - getAllCursorsStart,
    });
  }

  progress = 20;
  options.onProgress(progress);

  const progressPerCursor = (100 - progress) / cursors.length;

  for await (const itemsResult of asyncPool(options.maxParallelRequests, cursors, async (cursor) => {
    const getNextBoardItemsWithRetriesStart = Date.now();

    if (options.logger) {
      options.logger(`Fetching items page with cursor ${cursor}`, 'info');
    }

    const result = await getNextBoardItemsWithRetries(
      mondayClient,
      cursor,
      options.itemsPerPage,
      3,
      options.withSubitems,
      options.baseBoardFieldsOptions,
    );

    if (options.onMetric) {
      options.onMetric({
        name: 'getNextBoardItemsWithRetries',
        value: Date.now() - getNextBoardItemsWithRetriesStart,
      });
    }

    return result;
  })) {
    if (options.abortSignal?.aborted) {
      throw new AbortedError('Aborted loading board items');
    }

    allItems.push(...itemsResult);
    progress += progressPerCursor;
    options.onProgress(Math.min(Math.ceil(progress), 100));
  }

  options.onProgress(100);

  if (options.removeDuplicates) {
    return _.uniqBy(allItems, 'id');
  }

  return allItems;
}

export async function getItemsFromMultipleBoards(
  mondayClient: MondayClient,
  boardIdsAndOptions: { id: string; boardId: number; options: Partial<GetBoardItemsOptions> }[],
  logger?: (message: string, type: 'info' | 'warning' | 'error') => void,
  fallbackOptionsOverride: Partial<GetBoardItemsOptions> = { itemsPerPage: 50 }, // by default, try it with less items per page the second time
) {
  const idsToItems = new Map<string, BoardItem[]>();

  const progressPerBoard = 1 / boardIdsAndOptions.length;

  for await (const entry of boardIdsAndOptions) {
    const options = { ...getBoardItemsDefaultOptions, ...(entry.options || {}) };

    if (options.abortSignal?.aborted) {
      throw new AbortedError('Aborted loading board items');
    }

    if (logger) {
      logger(`Started fetching items for board ${entry.boardId}`, 'info');
    }

    try {
      const items = await getBoardItems(mondayClient, entry.boardId, {
        ...options,
        onProgress: (progress) => {
          options.onProgress(Math.min(100, Math.ceil(idsToItems.size * progressPerBoard * 100 + progressPerBoard * progress)));
        },
      });

      idsToItems.set(entry.id, items);

      if (logger) {
        logger(`Fetched ${items.length} items of board ${entry.boardId}`, 'info');
      }
    } catch (err) {
      console.log(`Failed to fetch items for board ${entry.boardId}. Trying a second time.`, err);

      if (logger) {
        logger(`Failed to fetch items for board ${entry.boardId}. Trying a second time.`, 'info');
      }

      try {
        const items = await getBoardItems(mondayClient, entry.boardId, {
          ...options,
          ...fallbackOptionsOverride,
          onProgress: (progress) => {
            options.onProgress(Math.min(100, Math.ceil(idsToItems.size * progressPerBoard * 100 + progressPerBoard * progress)));
          },
        });

        idsToItems.set(entry.id, items);

        if (logger) {
          logger(`Fetched ${items.length} items of board ${entry.boardId}`, 'info');
        }
      } catch (err2) {
        if (logger) {
          logger(`Failed to fetch items for board ${entry.boardId}.`, 'error');
        }

        try {
          // @ts-ignore
          err2.boardId = entry.boardId;
        } catch (err3) {}

        throw err2;
      }
    }
  }

  return idsToItems;
}

export type BoardGroup = {
  id: string;
  title: string;
  position: string;
};

export type Workspace = {
  id: number;
  name: string;
};

export type BoardBasic = {
  id: number;
  board_kind: 'public' | 'private' | 'share';
  name: string;
  description: string;
  item_terminology: string;
  updated_at: string;
  type: string;
  board_folder_id: number | null;
  workspace: Workspace;
  url: string;
};

export type BoardColumn = {
  id: string;
  archived: boolean;
  settings_str: string;
  title: string;
  description: string | null;
  type: ColumnType;
  width?: number | null;
};

export type BoardColumnCompact = {
  id: string;
  settings_str: string;
  type: ColumnType;
};

export type Board = BoardBasic & {
  columns: BoardColumn[];
  groups?: BoardGroup[];
};

const boardBasicFields = `
  id
  board_kind
  name
  description
  item_terminology
  type
  updated_at
  board_folder_id
  url
  workspace {
    id
    name
  }
`;

const boardColumnsFields = `
  columns {
    archived
    id
    settings_str
    title
    description
    type
  }
`;

const boardFields = `
  ${boardBasicFields}
  groups {
    id
    title
    position
  }
  ${boardColumnsFields}
`;

export const itemUpdateBasicFields = `
  body
  text_body
  id
  created_at
  creator {
    name
    id
  }
  assets {
    ${assetBaseFields}
  }
  replies {
    body
    text_body
    kind
    id
    created_at
    creator {
      name
      id
    }
  }
`;

const boardFieldsWithoutGroups = `
  ${boardBasicFields}
  ${boardColumnsFields}
`;

type GetBoardsOptions = {
  withoutGroups: boolean;
};

export async function getBoards(mondayClient: MondayClient, boardIds: number[], optionsArg: Partial<GetBoardsOptions> = {}) {
  if (boardIds.length > 100) {
    throw new Error(`Can't fetch more than 100 boards at a time`);
  }

  const result = await mondayClient.query(
    `
    query getBoards($boardIds: [ID!]!) {
      complexity {
        query
      }
      boards(ids: $boardIds, limit: 100) {
        ${optionsArg.withoutGroups ? boardFieldsWithoutGroups : boardFields}
      }
    }
  `,
    { boardIds },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<Board[]>(result, 'boards', (boards) => boards.map((board: any) => idToNumber(board)));
}

export async function getBoard(mondayClient: MondayClient, boardId: number, optionsArg: Partial<GetBoardsOptions> = {}) {
  const boards = await getBoards(mondayClient, [boardId], optionsArg);

  if (boards.length === 0) {
    throw new Error(`Board ${boardId} not found. Either it doesn't exist or you don't have access to it.`);
  }

  return boards[0]!;
}

export async function getBoardColumns(mondayClient: MondayClient, boardId: number) {
  const result = await mondayClient.query(
    `
    query getBoardColumns($boardIds: [ID!]!) {
      complexity {
        query
      }
      boards(ids: $boardIds, limit: 1) {
        columns {
          id
          settings_str
          type
        }
      }
    }
  `,
    { boardIds: [boardId] },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  const results = extractApiResult<BoardColumnCompact[][]>(result, 'boards', (boards) => boards.map((board: any) => board.columns));

  if (!results || results.length === 0) {
    throw new Error(`Board ${boardId} not found`);
  }

  return results[0]!;
}

// TODO: filter out boards that are not of type 'sub_items_board' or 'board' when calling this function
export async function getAllBoards(mondayClient: MondayClient, abortSignal?: AbortSignal) {
  return await getAllResults(
    async (page, limit) => {
      const result = await mondayClient.query(
        `
          query getBoards($page: Int!, $limit: Int!) {
            complexity {
              query
            }
            boards(page: $page, limit: $limit, order_by: used_at) {
              ${boardBasicFields}
            }
          }
        `,
        { page, limit },
      );

      if (result.type === 'error') {
        throw new MondayRequesterError(result);
      }

      return extractApiResult<BoardBasic[]>(result, 'boards', (boards) => boards.map((board: any) => idToNumber(board)));
    },
    1000,
    abortSignal,
  );
}

export async function addFileToFileColumn(mondayClient: MondayClient, itemId: number, columnId: string, file: File) {
  const result = await mondayClient.query(
    `
    mutation addFileToFileColumn($itemId: ID!, $columnId: String!, $file: File!) {
      complexity {
        query
      }
      add_file_to_column(item_id: $itemId, column_id: $columnId, file: $file) {
        id
      }
    }
  `,
    { itemId, columnId, file },
    undefined,
    { isFileUpload: true },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult(result, 'add_file_to_column.id') as string;
}

export async function clearFileColumn(mondayClient: MondayClient, boardId: number, itemId: number, columnId: string) {
  const result = await mondayClient.query(
    `
    mutation clearFileCoumn($boardId: ID!, $itemId: ID!, $columnId: String!) {
      complexity {
        query
      }
      change_column_value(board_id: $boardId, item_id: $itemId, column_id: $columnId, value: "{\\"clear_all\\": true}") {
        id
      }
    }
  `,
    { boardId, itemId, columnId },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult(result, 'change_column_value.id') as string;
}

export async function addFileToUpdate(mondayClient: MondayClient, updateId: number, file: File) {
  const result = await mondayClient.query(
    `
    mutation addFileToUpdate($updateId: ID!, $file: File!) {
      complexity {
        query
      }
      add_file_to_update(update_id: $updateId, file: $file) {
        id
      }
    }
  `,
    { updateId, file },
    undefined,
    { isFileUpload: true },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult(result, 'add_file_to_update.id') as string;
}

export async function addUpdate(mondayClient: MondayClient, itemId: number, body: string) {
  const result = await mondayClient.query(
    `
    mutation addUpdate($itemId: ID!, $body: String!) {
      complexity {
        query
      }
      create_update (item_id: $itemId, body: $body) {
        id
      }
    }
  `,
    { itemId, body },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult(result, 'create_update.id') as string;
}

export async function deleteUpdate(mondayClient: MondayClient, updateId: number) {
  const result = await mondayClient.query(
    `
    mutation deleteUpdate($updateId: ID!) {
      complexity {
        query
      }
      delete_update (id: $updateId) {
        id
      }
    }
  `,
    { updateId },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult(result, 'delete_update.id') as string;
}

export async function addReply(mondayClient: MondayClient, itemId: number, parentId: string, body: string) {
  const result = await mondayClient.query(
    `
    mutation addUpdate($itemId: ID!, $parentId: ID!, $body: String!) {
      complexity {
        query
      }
      create_update (item_id: $itemId, body: $body, parent_id: $parentId) {
        id
      }
    }
  `,
    { itemId, parentId, body },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult(result, 'create_update.id') as string;
}

export async function updateItemValue(mondayClient: MondayClient, boardId: number, itemId: number, columnId: string, value: any) {
  const result = await mondayClient.query(
    `
    mutation updateValue($boardId: ID!, $itemId: ID!, $columnId: String!, $value: String!) {
      complexity {
        query
      }
      change_simple_column_value (board_id: $boardId, item_id: $itemId, column_id: $columnId, value: $value) {
        id
      }
    }
  `,
    { boardId, itemId, columnId, value },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult(result, 'change_simple_column_value');
}

export async function createNotification(mondayClient: MondayClient, boardId: number, userId: number, text: string) {
  const result = await mondayClient.query(
    `
    mutation createNotification($userId: ID!, $boardId: ID!, $text: String!) {
      complexity {
        query
      }
      create_notification (user_id: $userId, target_id: $boardId, text: $text, target_type: Project) {
        text
      }
    }
  `,
    { boardId, userId, text },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult(result, 'create_notification');
}

export async function createConnectBoardColumn(mondayClient: MondayClient, boardId: number, title: string, connectedBoardId: number) {
  // NOTE: The `defaults` parameter must be inlined in the GraphQL query for board_relation columns.
  // Passing it as a JSON variable is silently ignored by the monday.com API.
  const result = await mondayClient.query(
    `
    mutation createConnectBoardColumn($boardId: ID!, $title: String!) {
      complexity {
        query
      }
      create_column(board_id: $boardId, title: $title, column_type: board_relation, defaults: { boardIds: [${connectedBoardId}] }) {
        id
        title
        type
      }
    }
  `,
    {
      boardId,
      title,
    },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<{ id: string; title: string; type: string }>(result, 'create_column');
}

export type Folder = {
  id: number;
  name: string;
  parent: {
    id: number;
    name: string;
  };
  workspace: {
    id: number;
    name: string;
  };
};

export async function getFolders(mondayClient: MondayClient, folderIds: number[]) {
  if (folderIds.length > 100) {
    throw new Error(`Can't fetch more than 100 folders at a time`);
  }

  const result = await mondayClient.query(
    `
    query getFolders($folderIds: [ID!]!) {
      complexity {
        query
      }
      folders(ids: $folderIds, limit: 100) {
        id
        name
        parent {
          id
          name
        }
        workspace {
          id
          name
        }
      }
    }
  `,
    { folderIds },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<Folder[]>(result, 'folders');
}

export type AppSubscription = {
  billing_period: string;
  days_left: number;
  is_trial: boolean;
  plan_id: string;
  renewal_date: string;
};

export async function getSubscription(mondayClient: MondayClient) {
  const result = await mondayClient.query(
    `
      query {
        complexity {
          query
        }
        app_subscription {
          billing_period
          days_left
          is_trial
          plan_id
          renewal_date
        }
      }
    `,
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return _.get(result, 'data.app_subscription.0') as AppSubscription | undefined;
}

export type SubscriptionDiscount = {
  discount_model_type: string; // nominal | percent
  discount_type: string; // one_time | recurring
  value: number;
};

export type AppSubscriptionDetails = {
  account_id: number;
  currency: string;
  days_left: number;
  discounts: SubscriptionDiscount[];
  end_date: string | null;
  max_units: number | null;
  monthly_price: number;
  period_type: string; // monthly | yearly
  plan_id: string;
  pricing_version_id: number;
  renewal_date: string | null;
  status: string; // active | inactive
};

export type AppSubscriptionsPage = {
  cursor: string | null;
  total_count: number;
  subscriptions: AppSubscriptionDetails[];
};

/**
 * A single page of the monday `app_subscriptions` (plural) query — an app's subscriptions across accounts.
 * Requires an app-level access token (developer tokens are rejected). `cursor` is null on the last page.
 *
 * The full `AppSubscriptionDetails` field set below (e.g. `max_units`) only exists from API version 2026-07
 * onward, so the query is pinned to that version regardless of the requester's default.
 */
export async function getAppSubscriptions(
  mondayClient: MondayClient,
  variables: {
    appId: string;
    cursor?: string | null;
    limit?: number;
    status?: 'active' | 'inactive';
    accountId?: number;
    apiVersion?: string;
  },
): Promise<AppSubscriptionsPage> {
  const result = await mondayClient.query(
    `
      query ($appId: ID!, $cursor: String, $limit: Int, $status: SubscriptionStatus, $accountId: Int) {
        complexity {
          query
        }
        app_subscriptions(app_id: $appId, cursor: $cursor, limit: $limit, status: $status, account_id: $accountId) {
          cursor
          total_count
          subscriptions {
            account_id
            currency
            days_left
            end_date
            max_units
            monthly_price
            period_type
            plan_id
            pricing_version_id
            renewal_date
            status
            discounts {
              discount_model_type
              discount_type
              value
            }
          }
        }
      }
    `,
    {
      appId: variables.appId,
      cursor: variables.cursor ?? null,
      limit: variables.limit ?? 500,
      status: variables.status,
      accountId: variables.accountId,
    },
    undefined,
    { apiVersion: variables.apiVersion ?? '2026-07' },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<AppSubscriptionsPage>(result, 'app_subscriptions');
}

export type User = {
  id: number;
  name: string;
  email: string;
  photo_small: string;
  time_zone_identifier: string;
};

const userFields = `
  id
  name
  email
  photo_small
  time_zone_identifier
`;

export async function getMe(mondayClient: MondayClient) {
  const result = await mondayClient.query(
    `
    query {
      complexity {
        query
      }
      me {
        ${userFields}
      }
    }
  `,
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<User>(result, 'me');
}

export type Account = {
  id: number;
  slug: string;
};

export async function getAccount(mondayClient: MondayClient) {
  const result = await mondayClient.query(
    `
    query {
      complexity {
        query
      }
      account {
        id
        slug
      }
    }
  `,
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<Account>(result, 'account');
}

export async function getUsers(mondayClient: MondayClient, userIds: number[]) {
  if (userIds.length > 200) {
    throw new Error(`Can't fetch more than 200 users at a time`);
  }

  const result = await mondayClient.query(
    `
    query getUsers($userIds: [ID!]!) {
      complexity {
        query
      }
      users(limit: 200, ids: $userIds) {
        ${userFields}
      }
    }
  `,
    { userIds },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<User[]>(result, 'users');
}

export async function getUsersByTeamId(mondayClient: MondayClient, teamId: number) {
  const result = await mondayClient.query(
    `
    query getUsersByTeamId($teamId: ID!) {
      complexity {
        query
      }
      teams(ids: [$teamId]) {
        users(limit: 200) {
          ${userFields}
        }
      }
    }
  `,
    { teamId },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<User[]>(result, 'teams[0].users');
}

export async function getBoardSubscribers(mondayClient: MondayClient, boardId: number) {
  const result = await mondayClient.query(
    `
    query getBoardSubscribers($boardId: ID!) {
      complexity {
        query
      }
      boards(ids: [$boardId]) {
        subscribers {
          ${userFields}
        }
      }
    }
  `,
    { boardId },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<User[]>(result, 'boards[0].subscribers');
}

export async function getBoardOwners(mondayClient: MondayClient, boardId: number) {
  const result = await mondayClient.query(
    `
    query getBoardSubscribers($boardId: ID!) {
      complexity {
        query
      }
      boards(ids: [$boardId]) {
        owners {
          ${userFields}
        }
      }
    }
  `,
    { boardId },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<User[]>(result, 'boards[0].owners');
}

export async function getBoardItemSubscribers(mondayClient: MondayClient, itemId: number) {
  const result = await mondayClient.query(
    `
    query getBoardItemSubscribers($itemId: ID!) {
      complexity {
        query
      }
      items(ids: [$itemId]) {
        subscribers {
          ${userFields}
        }
      }
    }
  `,
    { itemId },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<User[]>(result, 'items[0].subscribers');
}

export async function getBoardItem(
  mondayClient: MondayClient,
  itemId: number,
  additionalFields = '',
  baseBoardFieldsOptions: BaseBoardFieldsOptions = {},
) {
  const result = await mondayClient.query(
    `
    query getBoardItem($itemId: ID!) {
      complexity {
        query
      }
      items(ids: [$itemId]) {
        ${getBaseBoardFields(baseBoardFieldsOptions)}
        ${additionalFields}
      }
    }
  `,
    { itemId },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<BoardItem>(result, 'items[0]', transformBoardItem);
}

export async function getBoardItemsByIds(
  mondayClient: MondayClient,
  itemIds: number[],
  additionalFields = '',
  baseBoardFieldsOptions: BaseBoardFieldsOptions = {},
) {
  const result = await mondayClient.query(
    `
    query getBoardItemsByIds($itemIds: [ID!]!) {
      complexity {
        query
      }
      items(ids: $itemIds) {
        ${getBaseBoardFields(baseBoardFieldsOptions)}
        ${additionalFields}
      }
    }
  `,
    { itemIds },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<BoardItem[]>(result, 'items', (items) => items.map(transformBoardItem));
}

type BoardItemUpdateReply = {
  body: string;
  text_body: string;
  id: string;
  created_at: string;
  creator: {
    name: string;
    id: string;
  } | null;
};

export type BoardItemUpdate = {
  body: string;
  text_body: string;
  id: string;
  created_at: string;
  creator: {
    name: string;
    id: string;
  } | null;
  assets: Asset[];
  replies: BoardItemUpdateReply[];
};

export async function getBoardItemUpdates(mondayClient: MondayClient, itemId: number, page: number, limit: number) {
  const result = await mondayClient.query(
    `
    query getBoardItemUpdates($itemId: ID!, $page: Int!, $limit: Int!) {
      complexity {
        query
      }
      items(ids: [$itemId]) {
        updates(page: $page, limit: $limit) {
          ${itemUpdateBasicFields}
        }
      }
    }
  `,
    { itemId, page, limit },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<BoardItemUpdate[]>(result, 'items[0].updates');
}

export async function getBoardItemUpdate(mondayClient: MondayClient, itemId: number, updateId: number) {
  const result = await mondayClient.query(
    `
    query getBoardItemUpdates($itemId: ID!, $updateId: ID!) {
      complexity {
        query
      }
      items(ids: [$itemId]) {
        updates(ids: [$updateId] page: 1, limit: 1) {
          ${itemUpdateBasicFields}
        }
      }
    }
  `,
    { itemId, updateId },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<BoardItemUpdate>(result, 'items[0].updates[0]');
}

export async function getAssets(mondayClient: MondayClient, assetIds: number[]) {
  const result = await mondayClient.query(
    `
    query getAssets($assetIds: [ID!]!) {
      complexity {
        query
      }
      assets(ids: $assetIds) {
        ${assetBaseFields}
      }
    }
  `,
    { assetIds },
  );

  if (result.type === 'error') {
    throw new MondayRequesterError(result);
  }

  return extractApiResult<Asset[]>(result, 'assets');
}

type OAuthToken = {
  token_type: 'bearer';
  access_token: string;
  scope: string;
};

export async function getOauthToken(clientId: string, clientSecret: string, code: string, redirectUri: string) {
  const response = await fetch('https://auth.monday.com/oauth2/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      client_id: clientId,
      client_secret: clientSecret,
      code,
      redirect_uri: redirectUri,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get oauth token: ${response.statusText}`);
  }

  const data = await response.json();

  return data as OAuthToken;
}
